import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, isSessionError } from "@/lib/api";
import { emptyPermissions } from "@/lib/staff";
import { PermissionMatrix } from "@/components/admin/PermissionMatrix";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const GroupCard = ({ group, onSaved, onDeleted }) => {
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description || "");
  const [perms, setPerms] = useState(group.permissions);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/staff/groups/${group.id}`, { name, description, permissions: perms });
      toast.success("Bagian disimpan — akses anggota langsung diperbarui");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/staff/groups/${group.id}`);
      toast.success("Bagian dihapus");
      onDeleted();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="rk-glass rounded-2xl p-5" data-testid={`group-card-${group.id}`}>
      <div className="grid gap-3 sm:grid-cols-2">
        <input value={name} onChange={(e) => setName(e.target.value)} className="rk-input" placeholder="Nama bagian" data-testid="group-name-input" />
        <input value={description} onChange={(e) => setDescription(e.target.value)} className="rk-input" placeholder="Deskripsi tugas" />
      </div>
      <div className="mt-4">
        <PermissionMatrix value={perms} onChange={setPerms} testidPrefix={`group-${group.id}`} />
      </div>
      <div className="mt-4 flex gap-2">
        <button onClick={save} disabled={busy} className="rk-btn-primary inline-flex items-center gap-2 text-sm" data-testid="group-save-btn">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Simpan
        </button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button className="rk-btn-ghost inline-flex items-center gap-2 text-xs text-rose-400" data-testid="group-delete-btn">
              <Trash2 size={13} /> Hapus
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent className="border-white/10 bg-[#0f0f14] text-white">
            <AlertDialogHeader>
              <AlertDialogTitle>Hapus bagian "{group.name}"?</AlertDialogTitle>
              <AlertDialogDescription>Anggota di bagian ini akan pindah ke hak akses pribadi masing-masing.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-white/10 bg-transparent">Batal</AlertDialogCancel>
              <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700" data-testid="group-delete-confirm">Hapus</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export const GroupsPanel = () => {
  const [groups, setGroups] = useState([]);
  const [newName, setNewName] = useState("");

  const load = useCallback(() => {
    api.get("/staff/groups").then((r) => setGroups(r.data.items)).catch((e) => !isSessionError(e) && toast.error(errMsg(e)));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      await api.post("/staff/groups", { name: newName.trim(), permissions: emptyPermissions() });
      setNewName("");
      toast.success("Bagian dibuat — atur hak aksesnya di bawah");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <section className="space-y-5" data-testid="groups-panel">
      <p className="max-w-3xl text-sm text-zinc-400">
        Bagian = kelompok tugas (mis. "Admin Transaksi", "Tim Laporan"). Anggota yang memakai mode <b>Ikuti Bagian</b> otomatis mendapat hak akses dari bagiannya.
      </p>
      <form onSubmit={create} className="flex max-w-lg gap-2">
        <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nama bagian baru" className="rk-input" data-testid="new-group-input" />
        <button type="submit" className="rk-btn-primary inline-flex items-center gap-1 whitespace-nowrap text-sm" data-testid="new-group-btn">
          <Plus size={14} /> Tambah
        </button>
      </form>
      {groups.length === 0 ? <p className="text-sm text-zinc-500">Belum ada bagian.</p> : null}
      <div className="grid gap-5 xl:grid-cols-2">
        {groups.map((g) => <GroupCard key={g.id} group={g} onSaved={load} onDeleted={load} />)}
      </div>
    </section>
  );
};
